import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { 
  FiInbox, FiPlay, FiCheck, FiClock, FiSearch,
  FiChevronRight, FiAlertCircle, FiZap, FiAlertTriangle,
  FiPackage, FiRefreshCw, FiMaximize2, FiMinimize2, FiMove,
  FiTarget, FiTrendingUp, FiAward, FiVolume2, FiVolumeX, FiX, FiExternalLink,
  FiMapPin, FiDollarSign, FiUser, FiInfo, FiMail, FiMessageSquare, FiShoppingCart, FiEye
} from 'react-icons/fi';
import TicketCard from './TicketCard';
import OrderStory from './OrderStory';
import TicketActionModal from './TicketActionModals';

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

function isTicketUrgent(ticket, order) {
  if (!order?.inStockDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const orderDate = new Date(order.inStockDate);
  orderDate.setHours(0, 0, 0, 0);
  const dayDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
  return dayDiff === 1;
}

function isTicketOverdue(ticket) {
  if (!ticket.dueAt) return false;
  return new Date(ticket.dueAt) < new Date();
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function TicketQueue({ 
  tickets = [], 
  orders = [],
  onStartTicket,
  onCompleteTicket,
  onPauseTicket,
  onClaimTicket,
  onTicketClick,
  onRefresh
}) {
  const { adminInfo } = useSelector(s => s.login);
  // Backend returns 'id' not '_id'
  const currentUserId = adminInfo?.id || adminInfo?._id;
  
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [expandedStory, setExpandedStory] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [draggedTicket, setDraggedTicket] = useState(null);
  const [dragOverZone, setDragOverZone] = useState(null);
  const [actionTicket, setActionTicket] = useState(null); // For ticket-specific modal
  const [showOrderModal, setShowOrderModal] = useState(false);

  // Create order lookup map
  const orderMap = useMemo(() => {
    const map = {};
    (orders || []).forEach(o => {
      if (o?._id) {
        map[o._id] = o;
      }
    });
    return map;
  }, [orders]);

  // Enrich tickets with urgency/overdue status
  const enrichedTickets = useMemo(() => {
    return (tickets || []).map(ticket => {
      const order = orderMap[ticket?.orderId];
      return {
        ...ticket,
        order,
        isUrgent: isTicketUrgent(ticket, order),
        isOverdue: isTicketOverdue(ticket),
      };
    });
  }, [tickets, orderMap]);

  // Helper to compare IDs (handles ObjectId vs string)
  const isSameId = (id1, id2) => {
    if (!id1 || !id2) return false;
    return String(id1) === String(id2);
  };

  // Separate unclaimed vs claimed tickets
  const unclaimedTickets = useMemo(() => {
    let unclaimed = enrichedTickets.filter(t => 
      !t.assignedTo && t.status !== 'completed' && t.status !== 'cancelled'
    );
    
    return unclaimed.sort((a, b) => {
      if (a.priority === 'P0' && b.priority !== 'P0') return -1;
      if (a.priority !== 'P0' && b.priority === 'P0') return 1;
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      if (a.isUrgent && !b.isUrgent) return -1;
      if (!a.isUrgent && b.isUrgent) return 1;
      const priorityOrder = { P0: 0, P1: 1, P2: 2 };
      const pDiff = (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
      if (pDiff !== 0) return pDiff;
      if (a.dueAt && b.dueAt) {
        return new Date(a.dueAt) - new Date(b.dueAt);
      }
      return 0;
    });
  }, [enrichedTickets]);

  // My assigned tickets
  const myTickets = useMemo(() => {
    console.log('[TicketQueue] Filtering myTickets:', {
      currentUserId,
      totalEnriched: enrichedTickets.length,
      sampleAssignedTo: enrichedTickets[0]?.assignedTo,
      sampleStatus: enrichedTickets[0]?.status
    });
    
    let mine = enrichedTickets.filter(t => 
      isSameId(t.assignedTo, currentUserId) && t.status !== 'completed' && t.status !== 'cancelled'
    );
    
    console.log('[TicketQueue] After filter - mine:', mine.length);
    
    if (filter === 'urgent') {
      mine = mine.filter(t => t.isUrgent || t.isOverdue || t.priority === 'P0');
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      mine = mine.filter(t => 
        t.name?.toLowerCase().includes(q) ||
        t.orderUniqueId?.toLowerCase().includes(q) ||
        t.type?.toLowerCase().includes(q)
      );
    }

    return mine.sort((a, b) => {
      // Prioritize messaging tickets (DRIVER_SUPPORT) at the top
      const messagingTypes = ['DRIVER_SUPPORT', 'INQUIRY_SUPPORT', 'GUEST_SUPPORT'];
      const aIsMessaging = messagingTypes.includes(a.ticketTypeId || a.type);
      const bIsMessaging = messagingTypes.includes(b.ticketTypeId || b.type);

      if (aIsMessaging && !bIsMessaging) return -1;
      if (!aIsMessaging && bIsMessaging) return 1;

      // Then overdue tickets
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;

      // Then urgent tickets
      if (a.isUrgent && !b.isUrgent) return -1;
      if (!a.isUrgent && b.isUrgent) return 1;

      // Finally priority order
      const priorityOrder = { P0: 0, P1: 1, P2: 2 };
      return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
    });
  }, [enrichedTickets, currentUserId, filter, searchQuery]);

  const workingTickets = useMemo(() => 
    myTickets.filter(t => t.status === 'in_progress'),
    [myTickets]
  );

  const queuedTickets = useMemo(() => 
    myTickets.filter(t => t.status !== 'in_progress'),
    [myTickets]
  );

  const storyTickets = useMemo(() => {
    if (!selectedOrderId) return [];
    return enrichedTickets.filter(t => t.orderId === selectedOrderId);
  }, [enrichedTickets, selectedOrderId]);

  const selectedOrder = selectedOrderId ? orderMap[selectedOrderId] : null;

  // Stats
  const stats = useMemo(() => ({
    unclaimed: unclaimedTickets.length,
    unclaimedUrgent: unclaimedTickets.filter(t => t.isUrgent || t.isOverdue || t.priority === 'P0').length,
    myTotal: myTickets.length,
    myUrgent: myTickets.filter(t => t.isUrgent).length,
    myOverdue: myTickets.filter(t => t.isOverdue).length,
    p0Count: unclaimedTickets.filter(t => t.priority === 'P0').length,
    p1Count: unclaimedTickets.filter(t => t.priority === 'P1').length,
  }), [unclaimedTickets, myTickets]);

  // Handle claim ticket
  const handleClaimTicket = useCallback((ticket) => {
    console.log('Claiming ticket:', ticket);
    onClaimTicket?.(ticket);
  }, [onClaimTicket]);

  // Handle start ticket - call backend AND open action modal
  const handleStartTicket = useCallback((ticket) => {
    console.log('Starting ticket:', ticket);
    onStartTicket?.(ticket); // Call backend
    setActionTicket(ticket); // Open the action modal
  }, [onStartTicket]);

  // Handle complete ticket from modal
  const handleCompleteFromModal = useCallback((ticket) => {
    onCompleteTicket?.(ticket);
    setActionTicket(null);
  }, [onCompleteTicket]);

  const handleOpenOrder = useCallback((ticket) => {
    setSelectedOrderId(ticket.orderId);
    setSelectedTicket(ticket);
    setExpandedStory(false);
    setShowOrderModal(true);
  }, []);

  const handleCloseOrderModal = useCallback(() => {
    setShowOrderModal(false);
    setSelectedOrderId(null);
    setSelectedTicket(null);
  }, []);

  // Drag and Drop handlers
  const handleDragStart = useCallback((e, ticket) => {
    setDraggedTicket(ticket);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ticket.id);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverZone('myQueue');
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverZone(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (draggedTicket) {
      handleClaimTicket(draggedTicket);
    }
    setDraggedTicket(null);
    setDragOverZone(null);
  }, [draggedTicket, handleClaimTicket]);

  const handleDragEnd = useCallback(() => {
    setDraggedTicket(null);
    setDragOverZone(null);
  }, []);

  return (
    <div className="flex h-full bg-gradient-to-br from-zinc-50 to-zinc-100">
      {/* ═══════════════════════════════════════════════════════════════════
          UNCLAIMED TICKETS LANE (Left Side - Warm, Inviting)
          ═══════════════════════════════════════════════════════════════════ */}
      <div className="w-[400px] flex-shrink-0 flex flex-col animated-mesh-bg relative overflow-hidden">
        {/* Animated background blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -left-20 w-64 h-64 bg-amber-200/30 rounded-full blur-3xl animate-float" />
          <div className="absolute top-1/2 -right-20 w-48 h-48 bg-orange-200/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '-3s' }} />
          <div className="absolute -bottom-10 left-1/4 w-56 h-56 bg-rose-200/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '-5s' }} />
        </div>

        {/* Unclaimed Header */}
        <div className="relative z-10 px-5 py-5 border-b border-amber-200/50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`
                w-12 h-12 rounded-2xl flex items-center justify-center
                ${stats.p0Count > 0 
                  ? 'bg-gradient-to-br from-[#DC2626] to-[#BE123C] ring-2 ring-[#7F1D1D]/50 shadow-[0_6px_16px_rgba(0,0,0,0.2)] animate-pulse' 
                  : 'bg-gradient-to-br from-[#FBBF24] to-[#F97316] ring-1 ring-[#92400E]/30 shadow-[0_4px_12px_rgba(0,0,0,0.15)]'}
              `}>
                <FiAlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-zinc-800">Unclaimed</h2>
                <p className="text-sm text-zinc-600">Drag to claim</p>
              </div>
            </div>
            
            {stats.unclaimed > 0 && (
              <div className="flex items-center gap-2">
                <span className={`
                  px-4 py-2 rounded-2xl text-lg font-bold
                  ${stats.p0Count > 0 
                    ? 'bg-[#B91C1C] text-white ring-2 ring-[#7F1D1D] ring-offset-2 ring-offset-[#FFFBEB] shadow-[0_6px_20px_rgba(0,0,0,0.2)] animate-pulse' 
                    : 'bg-white text-[#92400E] ring-1 ring-[#FDE68A] shadow-[0_4px_12px_rgba(0,0,0,0.08)]'}
                `}>
                  {stats.unclaimed}
                </span>
              </div>
            )}
          </div>

          {/* Priority Breakdown Pills */}
          {stats.unclaimed > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {stats.p0Count > 0 && (
                <PriorityPill priority="P0" count={stats.p0Count} />
              )}
              {unclaimedTickets.filter(t => t.isOverdue).length > 0 && (
                <span className="px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 bg-[#FED7AA] text-[#9A3412] ring-1 ring-[#FB923C]/60 shadow-[0_2px_8px_rgba(0,0,0,0.1)]">
                  <FiClock className="w-3.5 h-3.5" />
                  {unclaimedTickets.filter(t => t.isOverdue).length} Overdue
                </span>
              )}
              {stats.p1Count > 0 && (
                <PriorityPill priority="P1" count={stats.p1Count} />
              )}
            </div>
          )}
        </div>

        {/* Unclaimed Tickets List */}
        <div className="relative z-10 flex-1 overflow-y-auto p-4 space-y-3">
          {unclaimedTickets.length === 0 ? (
            <EmptyState 
              icon={FiCheck}
              title="All Clear!"
              message="No unclaimed tickets"
              variant="success"
            />
          ) : (
            unclaimedTickets.map((ticket, idx) => (
              <DraggableTicketCard
                key={ticket.id}
                ticket={ticket}
                index={idx}
                isDragging={draggedTicket?.id === ticket.id}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onClaim={() => handleClaimTicket(ticket)}
                onViewOrder={() => handleOpenOrder(ticket)}
              />
            ))
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          MY TICKETS LANE (Main Area)
          ═══════════════════════════════════════════════════════════════════ */}
      <div 
        className={`
          flex-1 flex flex-col min-w-0 transition-all duration-300
          ${expandedStory ? 'w-1/2' : 'w-full'}
          ${dragOverZone === 'myQueue' ? 'bg-indigo-50' : 'bg-white'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Header */}
        <div className="bg-white border-b border-zinc-200 px-6 py-5">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
                <FiInbox className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-zinc-900">My Queue</h1>
                <p className="text-sm text-zinc-500">
                  {queuedTickets.length} pending • {workingTickets.length} in progress
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Sound Toggle */}
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`p-2.5 rounded-xl transition-colors ${
                  soundEnabled ? 'bg-indigo-50 text-indigo-600' : 'bg-zinc-100 text-zinc-400'
                }`}
                title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
              >
                {soundEnabled ? <FiVolume2 className="w-5 h-5" /> : <FiVolumeX className="w-5 h-5" />}
              </button>

              <button
                onClick={onRefresh}
                className="p-2.5 rounded-xl hover:bg-zinc-100 text-zinc-500 transition-colors"
                title="Refresh"
              >
                <FiRefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="flex items-center gap-3 mb-5">
            <StatPill 
              icon={FiTarget} 
              label="Total" 
              count={stats.myTotal} 
              color="indigo"
            />
            <StatPill 
              icon={FiZap} 
              label="Urgent" 
              count={stats.myUrgent} 
              color="amber"
              pulse={stats.myUrgent > 0}
            />
            <StatPill 
              icon={FiAlertCircle} 
              label="Overdue" 
              count={stats.myOverdue} 
              color="red"
              pulse={stats.myOverdue > 0}
            />
            <StatPill 
              icon={FiTrendingUp} 
              label="Working" 
              count={workingTickets.length} 
              color="emerald"
            />
          </div>

          {/* Filters & Search */}
          <div className="flex items-center gap-3">
            <div className="flex items-center p-1 bg-zinc-100 rounded-xl">
              <FilterTab 
                active={filter === 'all'} 
                onClick={() => setFilter('all')}
                label="All"
              />
              <FilterTab 
                active={filter === 'urgent'} 
                onClick={() => setFilter('urgent')}
                label="Urgent"
                badge={stats.myUrgent + stats.myOverdue}
              />
            </div>

            <div className="relative flex-1 max-w-xs">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search tickets..."
                className="w-full pl-10 pr-4 py-2.5 bg-zinc-100 border-0 rounded-xl text-sm placeholder:text-zinc-400 focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
              />
            </div>
          </div>
        </div>

        {/* Drop Zone Indicator */}
        {draggedTicket && (
          <div className={`
            mx-6 mt-4 p-4 rounded-2xl border-2 border-dashed transition-all
            ${dragOverZone === 'myQueue' 
              ? 'border-indigo-400 bg-indigo-50 opacity-100' 
              : 'border-zinc-300 bg-zinc-50 opacity-60'}
          `}>
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-indigo-600">
              <FiMove className="w-4 h-4" />
              Drop here to claim ticket
            </div>
          </div>
        )}

        {/* Queue Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Working Now Section */}
          {workingTickets.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-xl">
                  <FiPlay className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-700">WORKING NOW</span>
                </div>
                <span className="text-sm text-zinc-500">{workingTickets.length} active</span>
              </div>
              <div className="space-y-3">
                {workingTickets.map(ticket => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    isUrgent={ticket.isUrgent}
                    isOverdue={ticket.isOverdue}
                    isWorking={true}
                    onComplete={() => onCompleteTicket?.(ticket)}
                    onPause={() => onPauseTicket?.(ticket)}
                    onViewOrder={() => handleOpenOrder(ticket)}
                    onClick={() => onTicketClick?.(ticket)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Queue Section */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-100 rounded-xl">
                <FiInbox className="w-4 h-4 text-zinc-600" />
                <span className="text-sm font-semibold text-zinc-700">MY QUEUE</span>
              </div>
              <span className="text-sm text-zinc-500">{queuedTickets.length} waiting</span>
            </div>
            
            {queuedTickets.length === 0 ? (
              <EmptyState
                icon={stats.unclaimed > 0 ? FiInbox : FiAward}
                title={stats.unclaimed > 0 ? "Queue Empty" : "All caught up!"}
                message={stats.unclaimed > 0 
                  ? `${stats.unclaimed} unclaimed tickets waiting`
                  : "Great job! No pending tickets"
                }
                variant={stats.unclaimed > 0 ? "neutral" : "success"}
              />
            ) : (
              <div className="space-y-3">
                {queuedTickets.map((ticket, idx) => (
                  <div
                    key={ticket.id}
                    className="animate-slide-in-up"
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <TicketCard
                      ticket={ticket}
                      isUrgent={ticket.isUrgent}
                      isOverdue={ticket.isOverdue}
                      isWorking={false}
                      onStart={() => handleStartTicket(ticket)}
                      onViewOrder={() => handleOpenOrder(ticket)}
                      onClick={() => onTicketClick?.(ticket)}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          ORDER STORY PANEL (Slide-in)
          ═══════════════════════════════════════════════════════════════════ */}
      {selectedOrderId && !showOrderModal && (
        <div className={`
          border-l border-zinc-200 bg-white shadow-xl
          transition-all duration-300 overflow-hidden
          ${expandedStory ? 'w-[480px]' : 'w-0'}
        `}>
          <div className="h-full overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="font-bold text-zinc-900 flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-violet-100 flex items-center justify-center">
                  <FiPackage className="w-4 h-4 text-violet-600" />
                </div>
                Order Story
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setExpandedStory(!expandedStory)}
                  className="p-2 rounded-xl hover:bg-zinc-100 text-zinc-500 transition-colors"
                >
                  {expandedStory ? <FiMinimize2 className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => {
                    setSelectedOrderId(null);
                    setExpandedStory(false);
                  }}
                  className="p-2 rounded-xl hover:bg-zinc-100 text-zinc-500 transition-colors"
                >
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-5">
              <OrderStory
                order={selectedOrder}
                tickets={storyTickets}
                onTicketAction={(action, ticket) => {
                  if (action === 'start') onStartTicket?.(ticket);
                  else if (action === 'complete') onCompleteTicket?.(ticket);
                  else if (action === 'pause') onPauseTicket?.(ticket);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal for Order Context & Story */}
      {showOrderModal && selectedOrder && (
        <OrderContextModal
          order={selectedOrder}
          tickets={storyTickets}
          activeTicket={selectedTicket}
          onClose={handleCloseOrderModal}
          onOpenTicketAction={(ticket) => setActionTicket(ticket)}
        />
      )}

      {/* Ticket-Specific Action Modal */}
      {actionTicket && (
        <TicketActionModal
          ticket={actionTicket}
          order={orderMap[actionTicket.orderId]}
          onClose={() => setActionTicket(null)}
          onComplete={() => {
            onCompleteTicket?.(actionTicket);
            setActionTicket(null);
          }}
          onSaveData={async (data) => {
            // This would call the backend to save ticket-specific data
            console.log('Saving ticket data:', data);
          }}
          onEscalate={(ticket) => {
            console.log('Escalating ticket:', ticket);
            setActionTicket(null);
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function DraggableTicketCard({ ticket, index, isDragging, onDragStart, onDragEnd, onClaim, onViewOrder }) {
  const [isHovered, setIsHovered] = useState(false);
  
  // Higher contrast styles - solid backgrounds for better readability
  const priorityStyles = {
    P0: {
      border: 'border-l-[#B91C1C]',
      bg: 'bg-white',
      cardBorder: 'border border-[#FECACA]',
      badge: 'bg-[#B91C1C] text-white ring-1 ring-[#7F1D1D] shadow-[0_2px_6px_rgba(0,0,0,0.15)]',
      glow: 'shadow-[0_8px_24px_rgba(0,0,0,0.12)]',
      titleColor: 'text-[#18181B]',
      subtitleColor: 'text-[#B91C1C]',
    },
    P1: {
      border: 'border-l-[#D97706]',
      bg: 'bg-white',
      cardBorder: 'border border-[#FDE68A]',
      badge: 'bg-[#D97706] text-white ring-1 ring-[#92400E]/50 shadow-[0_2px_6px_rgba(0,0,0,0.12)]',
      glow: 'shadow-[0_6px_20px_rgba(0,0,0,0.1)]',
      titleColor: 'text-[#18181B]',
      subtitleColor: 'text-[#B45309]',
    },
    P2: {
      border: 'border-l-[#6366F1]',
      bg: 'bg-white',
      cardBorder: 'border border-[#E4E4E7]',
      badge: 'bg-[#3F3F46] text-white ring-1 ring-[#18181B]/50 shadow-[0_2px_6px_rgba(0,0,0,0.1)]',
      glow: 'shadow-[0_4px_12px_rgba(0,0,0,0.08)]',
      titleColor: 'text-[#18181B]',
      subtitleColor: 'text-[#4F46E5]',
    },
  };

  const style = priorityStyles[ticket.priority] || priorityStyles.P2;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, ticket)}
      onDragEnd={onDragEnd}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        ticket-enter rounded-2xl border-l-4 p-4 cursor-grab active:cursor-grabbing
        transition-all duration-200 relative
        ${style.border} ${style.bg} ${style.cardBorder}
        ${isDragging ? 'opacity-50 scale-95' : 'hover:scale-[1.02]'}
        ${ticket.isOverdue ? 'ring-2 ring-red-400 ring-offset-1' : ''}
        ${ticket.priority === 'P0' && !ticket.isOverdue ? 'ring-2 ring-red-300 ring-offset-1 animate-pulse' : ''}
        hover:shadow-xl ${style.glow}
      `}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Drag Handle */}
      <div className="absolute top-3 right-3 opacity-40 hover:opacity-100 transition-opacity">
        <FiMove className="w-4 h-4 text-zinc-500" />
      </div>

      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1 min-w-0 pr-6">
          <h3 className={`font-semibold text-sm truncate ${style.titleColor}`}>
            {ticket.name || ticket.type}
          </h3>
          <button 
            onClick={(e) => { e.stopPropagation(); onViewOrder?.(); }}
            className={`text-xs font-mono hover:underline ${style.subtitleColor}`}
          >
            Order #{ticket.orderUniqueId}
          </button>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`px-2.5 py-1 rounded-lg text-xs font-bold shadow-sm ${style.badge}`}>
            {ticket.priority}
          </span>
          <EffortBadge effort={ticket.effort || 3} />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <TimeDisplay ticket={ticket} />
        
        <button
          onClick={(e) => { e.stopPropagation(); onClaim?.(); }}
          className={`
            px-4 py-2 rounded-xl text-xs font-bold transition-all duration-200
            ${isHovered 
              ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 scale-105' 
              : 'bg-zinc-800 text-white hover:bg-zinc-700'}
          `}
        >
          Claim
        </button>
      </div>

      {/* Slack indicator */}
      {ticket.isSlackTicket && (
        <div className="absolute -top-2 -right-2">
          <span className="px-2 py-1 rounded-lg bg-[#7C3AED] text-white text-xs font-bold ring-1 ring-[#5B21B6] shadow-[0_4px_12px_rgba(0,0,0,0.2)] flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            SLACK
          </span>
        </div>
      )}
    </div>
  );
}

function EffortBadge({ effort }) {
  // Higher contrast effort badges with solid backgrounds and clear edges
  const colors = {
    1: 'bg-[#059669] text-white ring-1 ring-[#047857]',
    2: 'bg-[#0284C7] text-white ring-1 ring-[#0369A1]',
    3: 'bg-[#D97706] text-white ring-1 ring-[#B45309]',
    4: 'bg-[#EA580C] text-white ring-1 ring-[#C2410C]',
    5: 'bg-[#DC2626] text-white ring-1 ring-[#B91C1C]',
  };

  return (
    <span className={`
      w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shadow-[0_2px_4px_rgba(0,0,0,0.1)]
      ${colors[effort] || colors[3]}
    `}>
      {effort}
    </span>
  );
}

function TimeDisplay({ ticket }) {
  if (!ticket.dueAt) return null;
  
  const now = new Date();
  const due = new Date(ticket.dueAt);
  const diffMs = due - now;
  const diffMins = Math.floor(diffMs / 60000);
  
  let text, className;
  
  // Higher contrast time badges with solid backgrounds and clear edges
  if (diffMins < 0) {
    text = `${Math.abs(diffMins)}m overdue`;
    className = 'bg-[#B91C1C] text-white ring-1 ring-[#7F1D1D] shadow-[0_2px_6px_rgba(0,0,0,0.15)] animate-pulse';
  } else if (diffMins < 15) {
    text = `${diffMins}m left`;
    className = 'bg-[#EA580C] text-white ring-1 ring-[#C2410C] shadow-[0_2px_6px_rgba(0,0,0,0.12)]';
  } else if (diffMins < 60) {
    text = `${diffMins}m`;
    className = 'bg-[#D97706] text-white ring-1 ring-[#B45309] shadow-[0_2px_6px_rgba(0,0,0,0.1)]';
  } else {
    const hours = Math.floor(diffMins / 60);
    text = `${hours}h ${diffMins % 60}m`;
    className = 'bg-[#52525B] text-white ring-1 ring-[#3F3F46] shadow-[0_2px_6px_rgba(0,0,0,0.08)]';
  }

  return (
    <span className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold ${className}`}>
      <FiClock className="w-3.5 h-3.5" />
      {text}
    </span>
  );
}

function PriorityPill({ priority, count }) {
  // High contrast priority pills with clear edge definition
  const styles = {
    P0: 'bg-[#B91C1C] text-white ring-2 ring-[#7F1D1D] ring-offset-1 ring-offset-white shadow-[0_4px_14px_rgba(0,0,0,0.25)] animate-pulse',
    P1: 'bg-[#D97706] text-white ring-1 ring-[#92400E]/50 shadow-[0_3px_10px_rgba(0,0,0,0.18)]',
    P2: 'bg-[#3F3F46] text-white ring-1 ring-[#27272A] shadow-[0_2px_8px_rgba(0,0,0,0.12)]',
  };

  const labels = {
    P0: 'Critical',
    P1: 'High',
    P2: 'Normal',
  };

  return (
    <span className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 ${styles[priority]}`}>
      {priority === 'P0' && <FiZap className="w-3.5 h-3.5" />}
      {count} {labels[priority]}
    </span>
  );
}

function StatPill({ icon: Icon, label, count, color, pulse = false }) {
  const colors = {
    indigo: 'bg-[#E0E7FF] text-[#3730A3] ring-1 ring-[#A5B4FC]/60',
    amber: 'bg-[#FEF3C7] text-[#92400E] ring-1 ring-[#FCD34D]/60',
    red: 'bg-[#FEE2E2] text-[#991B1B] ring-1 ring-[#FCA5A5]/60',
    emerald: 'bg-[#D1FAE5] text-[#065F46] ring-1 ring-[#6EE7B7]/60',
    zinc: 'bg-[#E4E4E7] text-[#3F3F46] ring-1 ring-[#A1A1AA]/40',
  };

  return (
    <span className={`
      inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium
      ${colors[color]}
      ${pulse && count > 0 ? 'animate-pulse' : ''}
    `}>
      <Icon className="w-4 h-4" />
      <span className="font-bold">{count}</span>
      <span className="opacity-75">{label}</span>
    </span>
  );
}

function FilterTab({ active, onClick, label, badge }) {
  return (
    <button
      onClick={onClick}
      className={`
        px-4 py-2 rounded-xl text-sm font-medium transition-all
        ${active 
          ? 'bg-white text-[#18181B] shadow-[0_1px_3px_rgba(0,0,0,0.1)]' 
          : 'text-[#52525B] hover:text-[#18181B]'
        }
      `}
    >
      {label}
      {badge > 0 && (
        <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#DC2626] text-white text-xs font-bold ring-1 ring-[#B91C1C] shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
          {badge}
        </span>
      )}
    </button>
  );
}

function EmptyState({ icon: Icon, title, message, variant = 'neutral' }) {
  const variants = {
    success: 'bg-emerald-50 border-emerald-200',
    neutral: 'bg-zinc-50 border-zinc-200',
  };

  const iconColors = {
    success: 'text-emerald-500',
    neutral: 'text-zinc-400',
  };

  return (
    <div className={`text-center py-12 rounded-2xl border-2 border-dashed ${variants[variant]}`}>
      <Icon className={`w-12 h-12 mx-auto mb-4 ${iconColors[variant]}`} />
      <h3 className="font-semibold text-zinc-900 mb-1">{title}</h3>
      <p className="text-zinc-500 text-sm">{message}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ORDER CONTEXT MODAL
// ═══════════════════════════════════════════════════════════════════════════

function OrderContextModal({ order, tickets, activeTicket, onClose, onOpenTicketAction }) {
  const hasProducts = Array.isArray(order?.products) && order.products.length > 0;
  const statusChip = orderStatusChip(order?.status);
  const ticketForAction = activeTicket || (tickets?.[0] || null);
  const [activeTab, setActiveTab] = useState('action');
  const [arrivalTime, setArrivalTime] = useState(order?.storeArrivalTime || order?.arrivalTime || '');
  const [entryText, setEntryText] = useState(order?.entryInstructions || '');
  const [driverPay, setDriverPay] = useState(order?.driverPay || '');
  const [storeUrl, setStoreUrl] = useState(order?.storeOrderConfirmationUrl || '');
  const [storeSpend, setStoreSpend] = useState(order?.storeSpend || '');
  const [jobPlatform, setJobPlatform] = useState('');
  const [jobLink, setJobLink] = useState('');
  const [hireName, setHireName] = useState('');
  const [managerApproved, setManagerApproved] = useState(false);
  const [rescheduleConfirmed, setRescheduleConfirmed] = useState(false);
  const [notes, setNotes] = useState('');
  const [eventFilter, setEventFilter] = useState('all');

  const orderCity = order?.property?.address?.city || 'Unknown';
  const setTimeLabel = order?.storeArrivalTime || order?.arrivalTime || order?.deliveryWindow || 'Not set';
  const timelineEvents = buildTimelineEvents(order, tickets);
  const filteredEvents = eventFilter === 'all' ? timelineEvents : timelineEvents.filter(e => e.type === eventFilter);

  const tabButton = (key, label) => (
    <button
      key={key}
      onClick={() => setActiveTab(key)}
      className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
        activeTab === key ? 'bg-indigo-600 text-white shadow-sm' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 bg-gradient-to-r from-indigo-50 to-violet-50">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${priorityBadgeFromTicket(ticketForAction || {})}`}>
              {ticketForAction?.priority || 'P2'}
            </span>
            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${statusChip.className}`}>{statusChip.label}</span>
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
              <FiPackage className="w-4 h-4 text-indigo-500" />
              Order ID {order?.uniqueId || '—'}
            </div>
            <div className="flex items-center gap-1 text-xs text-zinc-500">
              <FiMapPin className="w-4 h-4" />
              {orderCity}
            </div>
            <span className="px-3 py-1 rounded-full bg-[#D1FAE5] text-[#065F46] text-xs font-semibold ring-1 ring-[#6EE7B7]/50">
              {setTimeLabel}
            </span>
            {isUrgent(order) && (
              <span className="px-2.5 py-1 rounded-full bg-[#B91C1C] text-white text-[11px] font-bold ring-1 ring-[#7F1D1D] shadow-[0_2px_6px_rgba(0,0,0,0.15)]">URGENT</span>
            )}
          </div>
          <button className="p-2 rounded-xl hover:bg-zinc-100 text-zinc-500" onClick={onClose}>
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 flex items-center gap-2 border-b border-zinc-200 bg-white">
          {tabButton('action', 'Ticket Action')}
          {tabButton('overview', 'Overview')}
          {tabButton('activity', 'Activity')}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'action' && (
            <div className="space-y-4">
              {!ticketForAction && (
                <EmptyState
                  icon={FiInfo}
                  title="No ticket selected"
                  message="Open from a ticket to see the action controls."
                  variant="neutral"
                />
              )}
              {ticketForAction && (
                <>
                  {/* Button to open ticket-specific full modal */}
                  <button
                    onClick={() => onOpenTicketAction?.(ticketForAction)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-indigo-500/25 transition-colors mb-4"
                  >
                    <FiMaximize2 className="w-4 h-4" />
                    Open {ticketForAction.name || ticketForAction.type} Action Panel
                  </button>
                  
                <TicketActionPanel
                  ticket={ticketForAction}
                  order={order}
                  arrivalTime={arrivalTime}
                  setArrivalTime={setArrivalTime}
                  entryText={entryText}
                  setEntryText={setEntryText}
                  driverPay={driverPay}
                  setDriverPay={setDriverPay}
                  storeUrl={storeUrl}
                  setStoreUrl={setStoreUrl}
                  storeSpend={storeSpend}
                  setStoreSpend={setStoreSpend}
                  notes={notes}
                  setNotes={setNotes}
                />
                </>
              )}
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <KeyTile label="Order ID" value={order?.uniqueId} icon={FiPackage} />
                <KeyTile label="City" value={orderCity} icon={FiMapPin} />
                <KeyTile label="Set Time" value={setTimeLabel} icon={FiClock} />
                <KeyTile label="Entry Instructions" value={order?.entryInstructions} icon={FiInfo} />
                <KeyTile label="Driver" value={order?.driverName || order?.driverID} icon={FiUser} />
                <KeyTile label="Driver Pay" value={order?.driverPay ? `$${order.driverPay}` : 'Not set'} icon={FiDollarSign} />
              </div>

              {/* Products shelf */}
              <div className="rounded-2xl border border-zinc-200 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-zinc-800">Products</h3>
                  <span className="text-xs text-zinc-500">{hasProducts ? `${order.products.length} items` : 'No products'}</span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {hasProducts ? order.products.map((p, idx) => (
                    <ProductCard key={idx} product={p} isPlaced={Boolean(order?.storeOrderConfirmationUrl)} />
                  )) : (
                    <p className="text-sm text-zinc-500">No products for this order.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {['all', 'ticket', 'refund', 'check', 'escalation'].map(key => (
                  <button
                    key={key}
                    onClick={() => setEventFilter(key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                      eventFilter === key ? 'bg-indigo-600 text-white' : 'bg-zinc-100 text-zinc-700'
                    }`}
                  >
                    {key === 'all' ? 'All' : key.charAt(0).toUpperCase() + key.slice(1)}
                  </button>
                ))}
              </div>
              <ActivityTimeline events={filteredEvents} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KeyTile({ label, value, icon: Icon, link }) {
  const ok = Boolean(value);
  return (
    <div className={`p-3 rounded-xl border shadow-sm flex items-center gap-2 ${ok ? 'border-emerald-100 bg-emerald-50' : 'border-amber-100 bg-amber-50'}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${ok ? 'bg-white text-emerald-600' : 'bg-white text-amber-600'}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold">{label}</p>
        <div className="flex items-center gap-1 text-sm font-semibold text-zinc-900 truncate">
          <span className="truncate">{value || 'Missing'}</span>
          {link && (
            <a href={link} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800">
              <FiExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductCard({ product, isPlaced }) {
  const status = productStatus(product, isPlaced);
  return (
    <div className={`min-w-[190px] max-w-[200px] p-3 rounded-2xl border shadow-sm bg-white ${status.ring}`}>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-zinc-100 overflow-hidden flex items-center justify-center">
          {product.imageUrl ? (
            <img src={product.imageUrl} alt={product.name || 'Product'} className="w-full h-full object-cover" />
          ) : (
            <FiPackage className="w-5 h-5 text-zinc-500" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-900 truncate">{product.name || 'Item'}</p>
          <p className="text-xs text-zinc-500">Qty: {product.quantity || product.qty || 1}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${status.chip}`}>{status.label}</span>
        {product.refundReason && (
          <span className="text-[11px] text-zinc-500 truncate max-w-[90px]" title={product.refundReason}>
            {product.refundReason}
          </span>
        )}
      </div>
    </div>
  );
}

function StoryTimeline({ tickets }) {
  if (!tickets || tickets.length === 0) {
    return <p className="text-sm text-zinc-500">No tickets for this order.</p>;
  }

  return (
    <div className="space-y-3 max-h-72 overflow-auto">
      {tickets.map((t, idx) => (
        <div key={t.id || idx} className="relative pl-6">
          <div className="absolute left-0 top-2 w-2 h-2 rounded-full bg-indigo-500" />
          <div className="p-3 rounded-xl border border-zinc-100 bg-white flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${priorityBadgeFromTicket(t)}`}>{t.priority}</span>
              <div>
                <p className="text-sm font-semibold text-zinc-900">{t.name || t.type}</p>
                <p className="text-xs text-zinc-500">Effort {t.effort || 3}</p>
              </div>
            </div>
            <span className={`px-2.5 py-1 rounded-lg text-[11px] font-bold ${statusChipForTicket(t)}`}>{t.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ActivityTimeline({ events }) {
  if (!events || events.length === 0) {
    return <p className="text-sm text-zinc-500">No activity yet.</p>;
  }

  return (
    <div className="relative">
      <div className="absolute left-3 top-0 bottom-0 w-px bg-gradient-to-b from-indigo-200 via-zinc-200 to-transparent" />
      <div className="space-y-3">
        {events.map((event, idx) => (
          <div key={idx} className="relative pl-8">
            <div className={`absolute left-1 top-2 w-4 h-4 rounded-full ${event.dot}`} />
            <div className="p-3 rounded-2xl border border-zinc-200 bg-white shadow-sm flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${event.iconBg}`}>
                  {event.icon}
                </div>
                <div>
                  <p className="text-sm font-semibold text-zinc-900">{event.title}</p>
                  {event.detail && <p className="text-xs text-zinc-500">{event.detail}</p>}
                </div>
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold">{event.time}</p>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${event.badge}`}>
                  {event.typeLabel}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TicketActionPanel({
  ticket,
  order,
  arrivalTime,
  setArrivalTime,
  entryText,
  setEntryText,
  driverPay,
  setDriverPay,
  storeUrl,
  setStoreUrl,
  storeSpend,
  setStoreSpend,
  jobPlatform,
  setJobPlatform,
  jobLink,
  setJobLink,
  hireName,
  setHireName,
  managerApproved,
  setManagerApproved,
  rescheduleConfirmed,
  setRescheduleConfirmed,
  notes,
  setNotes,
}) {
  const isUnclaimed = !ticket.assignedTo;
  const isValidUrl = (url) => /^https?:\/\//i.test(url);

  const Section = ({ title, children }) => (
    <div className="rounded-2xl border border-zinc-200 p-4 bg-white shadow-sm">
      <h3 className="text-sm font-semibold text-zinc-900 mb-3">{title}</h3>
      {children}
    </div>
  );

  const renderAction = () => {
    switch (ticket.type) {
      case 'SET_DELIVERY_TIME':
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Set the confirmed arrival window shown to the guest.</p>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs font-semibold text-zinc-600">In-stock date</label>
                <div className="mt-1 px-3 py-2 rounded-xl bg-zinc-50 border border-zinc-200 text-sm text-zinc-800">
                  {order?.inStockDate ? new Date(order.inStockDate).toDateString() : 'Not provided'}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Set time (storeArrivalTime)</label>
                <input
                  value={arrivalTime}
                  onChange={(e) => setArrivalTime(e.target.value)}
                  placeholder="e.g., 2:00 PM - 4:00 PM"
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Suggestion appears, but dispatcher must set it manually.</p>
              </div>
              <button className="self-start px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-sm hover:bg-indigo-700">
                Save time
              </button>
            </div>
          </>
        );
      case 'VERIFY_ENTRY':
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Confirm entry instructions are accurate and actionable.</p>
            <textarea
              value={entryText}
              onChange={(e) => setEntryText(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
              placeholder="Gate code, parking notes, contact steps…"
            />
            <div className="flex items-center gap-2 mt-2">
              <button className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-sm hover:bg-indigo-700">
                Save entry instructions
              </button>
              <span className="text-xs text-zinc-500">Mark this ticket complete after saving.</span>
            </div>
          </>
        );
      case 'FIND_DRIVER':
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Assign a driver and confirm pay for this order.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-zinc-600">Driver</label>
                <select className="w-full mt-1 px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm">
                  <option>Select driver</option>
                  <option>Sam G.</option>
                  <option>Alex P.</option>
                  <option>QA Ops</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Driver pay</label>
                <input
                  value={driverPay}
                  onChange={(e) => setDriverPay(e.target.value)}
                  type="number"
                  min="0"
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
                  placeholder="e.g., 32"
                />
              </div>
            </div>
            <button className="mt-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-sm hover:bg-indigo-700">
              Save driver
            </button>
          </>
        );
      case 'PLACE_ORDER':
        const urlOk = isValidUrl(storeUrl);
        const spendOk = Number(storeSpend) > 0;
        const disableComplete = !(urlOk && spendOk);
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Add the store order confirmation URL to confirm placement.</p>
            <input
              value={storeUrl}
              onChange={(e) => setStoreUrl(e.target.value)}
              placeholder="https://store.com/order/123"
              className="w-full px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
            />
            <p className="text-[11px] text-zinc-500 mt-1">Must be a valid http/https URL.</p>
            <div className="mt-3">
              <label className="text-xs font-semibold text-zinc-600">Store spend (total)</label>
              <input
                value={storeSpend}
                onChange={(e) => setStoreSpend(e.target.value)}
                type="number"
                min="0"
                step="0.01"
                placeholder="$0.00"
                className="w-full mt-1 px-3 py-2 rounded-xl border border-zinc-300 text-sm bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              />
              <p className="text-[11px] text-zinc-500 mt-1">Required to complete ticket.</p>
            </div>
            <div className="mt-4 rounded-2xl border border-zinc-200 p-3 bg-zinc-50">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-semibold text-zinc-900">Products to place</p>
                  <p className="text-xs text-zinc-500">Review quantities, note refunds, then add to cart.</p>
                </div>
                <button className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold shadow-sm hover:bg-emerald-700">
                  <FiShoppingCart className="w-4 h-4" />
                  Add all to cart
                </button>
              </div>
              <div className="space-y-2">
                {(order?.products || []).map((p, idx) => {
                  const refunded = p.refundApproved || p.refunded;
                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between rounded-xl border border-zinc-200 bg-white px-3 py-2"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-zinc-100 overflow-hidden flex items-center justify-center">
                          {p.imageUrl ? (
                            <img src={p.imageUrl} alt={p.name || 'Product'} className="w-full h-full object-cover" />
                          ) : (
                            <FiPackage className="w-4 h-4 text-zinc-500" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-zinc-900">{p.name || 'Item'}</p>
                          <p className="text-xs text-zinc-500">Qty: {p.quantity || p.qty || 1}</p>
                        </div>
                        {refunded && (
                          <span className="px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-[11px] font-semibold ring-1 ring-[#FCD34D]/50">
                            Refund {p.refundApproved ? 'approved' : 'pending'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 text-white shadow-sm hover:bg-amber-700"
                          type="button"
                        >
                          Mark refund
                        </button>
                        {refunded && (
                          <span className="text-[11px] text-amber-700">Sent for admin approval</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {(!order?.products || order.products.length === 0) && (
                  <p className="text-sm text-zinc-500">No products on this order.</p>
                )}
              </div>
            </div>
            <button
              className={`mt-2 px-4 py-2 rounded-xl text-sm font-semibold shadow-sm ${
                disableComplete ? 'bg-zinc-300 text-zinc-500 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
              disabled={disableComplete}
            >
              Save & complete
            </button>
            {!urlOk && <p className="text-[11px] text-red-600 mt-1">URL is required.</p>}
            {!spendOk && <p className="text-[11px] text-red-600 mt-1">Store spend must be greater than $0.</p>}
          </>
        );
      case 'DRIVER_SUPPORT':
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Respond in the shared Slack channel.</p>
            {ticket.slackThreadUrl ? (
              <a
                href={ticket.slackThreadUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-purple-600 text-white text-sm font-semibold shadow-sm hover:bg-purple-700"
              >
                <FiMessageSquare className="w-4 h-4" />
                Open Slack thread
              </a>
            ) : (
              <p className="text-sm text-zinc-500">No Slack thread linked.</p>
            )}
          </>
        );
      case 'CONTACT_HOST':
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Reach out to the host to confirm details.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <KeyTile label="Host Email" value={order?.guestEmail || 'Not provided'} icon={FiMail} />
              <KeyTile label="Entry Instructions" value={order?.entryInstructions || 'Missing'} icon={FiInfo} />
            </div>
            <div className="flex items-center gap-2 mt-2">
              <a
                href={order?.guestEmail ? `mailto:${order.guestEmail}` : undefined}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold shadow-sm ${
                  order?.guestEmail ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-zinc-200 text-zinc-500 cursor-not-allowed'
                }`}
              >
                <FiMail className="w-4 h-4" />
                Email host
              </a>
              {ticket.hostThreadUrl ? (
                <a
                  href={ticket.hostThreadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold shadow-sm bg-purple-600 text-white hover:bg-purple-700"
                >
                  <FiMessageSquare className="w-4 h-4" />
                  Open thread
                </a>
              ) : (
                <span className="text-xs text-zinc-500">No thread linked</span>
              )}
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full mt-2 px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
              placeholder="Log the outreach attempt and notes..."
            />
            <div className="flex items-center gap-2 mt-2">
              <button className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-sm hover:bg-indigo-700">
                Log contact
              </button>
              <span className="text-xs text-zinc-500">Mark complete after contact is logged.</span>
            </div>
          </>
        );
      case 'EOD_CHECK':
      case 'MORNING_CHECK':
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Run the checklist before approving.</p>
            <ul className="space-y-2 text-sm text-zinc-700">
              <li>• Pickup time matches grocer site</li>
              <li>• Entry instructions current; no host outreach flag</li>
              <li>• Delivery time accurate</li>
              <li>• Driver assigned correctly</li>
              <li>• Store order ID/URL entered</li>
              <li>• Total store spend entered</li>
              <li>• Driver pay set (if admin)</li>
            </ul>
            <div className="flex items-center gap-2 mt-3">
              <button className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold shadow-sm hover:bg-emerald-700">
                Approve checklist
              </button>
              <button className="px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-semibold shadow-sm hover:bg-amber-600">
                Report error
              </button>
            </div>
          </>
        );
      case 'POST_JOB_EXTERNAL': {
        const platformOk = Boolean(jobPlatform);
        const linkOk = isValidUrl(jobLink);
        const disablePost = !(platformOk && linkOk);
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Post the job externally and capture platform + link.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-zinc-600">Platform</label>
                <select
                  value={jobPlatform}
                  onChange={(e) => setJobPlatform(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-zinc-300 text-sm bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                >
                  <option value="">Select platform</option>
                  <option value="GigSmart">GigSmart</option>
                  <option value="TaskRabbit">TaskRabbit</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Job posting link</label>
                <input
                  value={jobLink}
                  onChange={(e) => setJobLink(e.target.value)}
                  placeholder="https://taskrabbit.com/job/..."
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
                />
                {!linkOk && jobLink && <p className="text-[11px] text-red-600 mt-1">Valid http/https link required.</p>}
              </div>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 mt-3 text-xs text-zinc-700">
              On completion, a follow-up ticket “New Hire” should be created to record the hire and vetting.
            </div>
            <button
              className={`mt-3 px-4 py-2 rounded-xl text-sm font-semibold shadow-sm ${
                disablePost ? 'bg-zinc-300 text-zinc-500 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
              disabled={disablePost}
            >
              Save posting
            </button>
          </>
        );
      }
      case 'NEW_HIRE':
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Record the hire from the external posting and confirm vetting.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-zinc-600">Hired person name</label>
                <input
                  value={hireName}
                  onChange={(e) => setHireName(e.target.value)}
                  placeholder="Full name"
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-zinc-700">
                  <input
                    type="checkbox"
                    className="rounded border-zinc-300"
                    checked={managerApproved}
                    onChange={(e) => setManagerApproved(e.target.checked)}
                  />
                  Vetted and confirmed they can perform the delivery
                </label>
              </div>
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full mt-3 px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
              placeholder="Notes on vetting, schedule, contact info…"
            />
          </>
        );
      case 'CANCEL_ORDER':
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Manager approval is required to cancel.</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
              placeholder="Reason for cancellation (required)"
            />
            <label className="flex items-center gap-2 mt-2 text-sm text-zinc-700">
              <input
                type="checkbox"
                className="rounded border-zinc-300"
                checked={managerApproved}
                onChange={(e) => setManagerApproved(e.target.checked)}
              />
              Manager approved
            </label>
            {!managerApproved && <p className="text-[11px] text-red-600 mt-1">Manager approval required.</p>}
          </>
        );
      case 'RESCHEDULE_ORDER':
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Capture the reason and confirm the reschedule with the guest.</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
              placeholder="Reason for reschedule"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs font-semibold text-zinc-600">New delivery window</label>
                <input
                  value={arrivalTime}
                  onChange={(e) => setArrivalTime(e.target.value)}
                  placeholder="e.g., Tomorrow 2-4pm"
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-zinc-700">
                  <input
                    type="checkbox"
                    className="rounded border-zinc-300"
                    checked={rescheduleConfirmed}
                    onChange={(e) => setRescheduleConfirmed(e.target.checked)}
                  />
                  Confirmed with guest
                </label>
              </div>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 mt-3 text-xs text-zinc-700">
              Follow-up: ensure a driver assignment ticket is created after reschedule is confirmed.
            </div>
          </>
        );
      case 'MONITOR_DRIVER':
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Monitor order acceptance without assigning a driver yet.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <KeyTile label="Driver views" value={ticket.views || 12} icon={FiEye} />
              <KeyTile label="Accepts" value={ticket.accepts || 1} icon={FiCheck} />
              <KeyTile label="Conversion" value={ticket.views ? `${Math.round((ticket.accepts || 1) / ticket.views * 100)}%` : '—'} icon={FiTrendingUp} />
            </div>
            <p className="text-xs text-zinc-500 mt-2">Escalate to Find Driver / Increase Pay / Post External Job if views are high and accepts are low.</p>
          </>
        );
      default:
        return (
          <>
            <p className="text-sm text-zinc-600 mb-3">Work this ticket from the ticket screen.</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-zinc-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-sm"
              placeholder="Notes or next steps..."
            />
          </>
        );
    }
  };

  return (
    <div className="space-y-3">
      {isUnclaimed && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm font-semibold">
          Claim to work on this ticket. Actions unlock after claiming.
        </div>
      )}
      <Section title={ticket.name || ticket.type || 'Ticket'}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${priorityBadgeFromTicket(ticket)}`}>
            {ticket.priority || 'P2'}
          </span>
          <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${statusChipForTicket(ticket)}`}>
            {ticket.status || 'pending'}
          </span>
          <span className="px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-700 text-[11px] font-semibold">
            Effort {ticket.effort || 3}
          </span>
          {ticket.isUrgent && (
            <span className="px-2.5 py-1 rounded-full bg-[#B91C1C] text-white text-[11px] font-bold ring-1 ring-[#7F1D1D] shadow-[0_2px_6px_rgba(0,0,0,0.15)]">Urgent</span>
          )}
        </div>
        {renderAction()}
      </Section>
    </div>
  );
}

// Helpers
function priorityBadge(order) {
  const priority = order?.priority || order?.priorityOverride;
  if (priority === 'P0') return 'bg-[#B91C1C] text-white ring-1 ring-[#7F1D1D]';
  if (priority === 'P1') return 'bg-[#D97706] text-white ring-1 ring-[#92400E]/50';
  return 'bg-[#3F3F46] text-white ring-1 ring-[#27272A]';
}

function priorityLabel(order) {
  const priority = order?.priority || order?.priorityOverride || 'P2';
  if (priority === 'P0') return 'P0 Critical';
  if (priority === 'P1') return 'P1 High';
  return 'P2 Normal';
}

function orderStatusChip(status) {
  switch ((status || '').toLowerCase()) {
    case 'requested': return { label: 'Requested', className: 'bg-[#DBEAFE] text-[#1E40AF] ring-1 ring-[#93C5FD]/50' };
    case 'pending': return { label: 'Pending', className: 'bg-[#FEF3C7] text-[#92400E] ring-1 ring-[#FCD34D]/50' };
    case 'picked': return { label: 'Picked', className: 'bg-[#E0E7FF] text-[#3730A3] ring-1 ring-[#A5B4FC]/50' };
    case 'delivered': return { label: 'Delivered', className: 'bg-[#D1FAE5] text-[#065F46] ring-1 ring-[#6EE7B7]/50' };
    default: return { label: status || 'Unknown', className: 'bg-[#F4F4F5] text-[#3F3F46] ring-1 ring-[#D4D4D8]/50' };
  }
}

function isUrgent(order) {
  if (!order?.inStockDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const orderDate = new Date(order.inStockDate);
  orderDate.setHours(0, 0, 0, 0);
  const dayDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
  return dayDiff === 1;
}

function productStatus(p, isPlaced) {
  // Default for overview context before placement: show guest preference
  if (!isPlaced && p.substituteOrRefund) {
    const prefersRefund = String(p.substituteOrRefund).toLowerCase().includes('refund');
    return {
      label: prefersRefund ? 'Prefers Refund' : 'Prefers Substitute',
      chip: prefersRefund ? 'bg-[#FEF3C7] text-[#92400E] ring-1 ring-[#FCD34D]/50' : 'bg-[#E0E7FF] text-[#3730A3] ring-1 ring-[#A5B4FC]/50',
      ring: prefersRefund ? 'ring-1 ring-[#FCD34D]/40' : 'ring-1 ring-[#A5B4FC]/40'
    };
  }
  // After placement: show refund workflow state
  if (p.refundApproved) return { label: 'Refund Approved', chip: 'bg-[#D1FAE5] text-[#065F46] ring-1 ring-[#6EE7B7]/50', ring: 'ring-1 ring-[#6EE7B7]/40' };
  if (p.refunded) return { label: 'Refund Pending', chip: 'bg-[#FEF3C7] text-[#92400E] ring-1 ring-[#FCD34D]/50', ring: 'ring-1 ring-[#FCD34D]/40' };
  return { label: 'Ready', chip: 'bg-[#F4F4F5] text-[#3F3F46] ring-1 ring-[#D4D4D8]/50', ring: '' };
}

function priorityBadgeFromTicket(t) {
  if (t.priority === 'P0') return 'bg-[#B91C1C] text-white ring-1 ring-[#7F1D1D]';
  if (t.priority === 'P1') return 'bg-[#D97706] text-white ring-1 ring-[#92400E]/50';
  return 'bg-[#3F3F46] text-white ring-1 ring-[#27272A]';
}

function statusChipForTicket(t) {
  if (t.status === 'completed') return 'bg-[#D1FAE5] text-[#065F46] ring-1 ring-[#6EE7B7]/50';
  if (t.status === 'in_progress') return 'bg-[#DBEAFE] text-[#1E40AF] ring-1 ring-[#93C5FD]/50';
  if (t.status === 'pending') return 'bg-[#FEF3C7] text-[#92400E] ring-1 ring-[#FCD34D]/50';
  return 'bg-[#F4F4F5] text-[#3F3F46] ring-1 ring-[#D4D4D8]/50';
}

function buildTimelineEvents(order, tickets) {
  const events = [];

  // Ticket lifecycle events
  tickets.forEach(t => {
    if (t.createdAt) {
      const ts = new Date(t.createdAt).getTime();
      events.push({
        title: `Ticket Created (${t.name || t.type})`,
        detail: `Priority ${t.priority || 'P2'}`,
        time: new Date(t.createdAt).toLocaleTimeString(),
        ts,
        type: 'ticket',
        typeLabel: 'Ticket',
        icon: <FiPackage className="w-4 h-4 text-indigo-600" />,
        iconBg: 'bg-indigo-100 text-indigo-700',
        badge: 'bg-indigo-100 text-indigo-700',
        dot: 'bg-indigo-500'
      });
    }
    if (t.startedAt) {
      const ts = new Date(t.startedAt).getTime();
      events.push({
        title: `Started ${t.name || t.type}`,
        detail: t.assignedToName ? `By ${t.assignedToName}` : undefined,
        time: new Date(t.startedAt).toLocaleTimeString(),
        ts,
        type: 'ticket',
        typeLabel: 'Ticket',
        icon: <FiPlay className="w-4 h-4 text-emerald-600" />,
        iconBg: 'bg-emerald-100 text-emerald-700',
        badge: 'bg-emerald-100 text-emerald-700',
        dot: 'bg-emerald-500'
      });
    }
    if (t.completedAt) {
      const ts = new Date(t.completedAt).getTime();
      events.push({
        title: `Completed ${t.name || t.type}`,
        detail: t.assignedToName ? `By ${t.assignedToName}` : undefined,
        time: new Date(t.completedAt).toLocaleTimeString(),
        ts,
        type: 'ticket',
        typeLabel: 'Ticket',
        icon: <FiCheck className="w-4 h-4 text-emerald-600" />,
        iconBg: 'bg-emerald-100 text-emerald-700',
        badge: 'bg-emerald-100 text-emerald-700',
        dot: 'bg-emerald-500'
      });
    }
    const esc = t.history?.find(h => h.action === 'escalated');
    if (esc?.timestamp) {
      const ts = new Date(esc.timestamp).getTime();
      events.push({
        title: `Escalated ${t.name || t.type}`,
        detail: esc.notes || 'Escalated',
        time: new Date(esc.timestamp).toLocaleTimeString(),
        ts,
        type: 'escalation',
        typeLabel: 'Escalation',
        icon: <FiAlertTriangle className="w-4 h-4 text-red-600" />,
        iconBg: 'bg-red-100 text-red-700',
        badge: 'bg-red-100 text-red-700',
        dot: 'bg-red-500'
      });
    }
  });

  // Morning/EOD checks
  if (order?.morningCheck?.checkedAt) {
    const ts = new Date(order.morningCheck.checkedAt).getTime();
    events.push({
      title: 'Morning Check',
      detail: order.morningCheck.checkedByName ? `By ${order.morningCheck.checkedByName}` : undefined,
      time: new Date(order.morningCheck.checkedAt).toLocaleTimeString(),
      ts,
      type: 'check',
      typeLabel: 'Check',
      icon: <FiCheck className="w-4 h-4 text-indigo-600" />,
      iconBg: 'bg-indigo-100 text-indigo-700',
      badge: 'bg-indigo-100 text-indigo-700',
      dot: 'bg-indigo-500'
    });
  }
  if (order?.eodReview?.reviewedAt) {
    const ts = new Date(order.eodReview.reviewedAt).getTime();
    events.push({
      title: 'EOD Review',
      detail: order.eodReview.reviewedByName ? `By ${order.eodReview.reviewedByName}` : undefined,
      time: new Date(order.eodReview.reviewedAt).toLocaleTimeString(),
      ts,
      type: 'check',
      typeLabel: 'Check',
      icon: <FiCheck className="w-4 h-4 text-violet-600" />,
      iconBg: 'bg-violet-100 text-violet-700',
      badge: 'bg-violet-100 text-violet-700',
      dot: 'bg-violet-500'
    });
  }

  // Refund events
  if (Array.isArray(order?.products)) {
    order.products.forEach((p, idx) => {
      if (p.refundedAt) {
        const ts = new Date(p.refundedAt).getTime();
        events.push({
          title: `Refund Marked (Item ${idx + 1})`,
          detail: p.refundReason || 'Refund requested',
          time: new Date(p.refundedAt).toLocaleTimeString(),
          ts,
          type: 'refund',
          typeLabel: 'Refund',
          icon: <FiAlertTriangle className="w-4 h-4 text-amber-600" />,
          iconBg: 'bg-amber-100 text-amber-700',
          badge: 'bg-amber-100 text-amber-700',
          dot: 'bg-amber-500'
        });
      }
      if (p.refundApprovedAt) {
        const ts = new Date(p.refundApprovedAt).getTime();
        events.push({
          title: `Refund Approved (Item ${idx + 1})`,
          detail: p.refundReason || 'Approved',
          time: new Date(p.refundApprovedAt).toLocaleTimeString(),
          ts,
          type: 'refund',
          typeLabel: 'Refund',
          icon: <FiCheck className="w-4 h-4 text-emerald-600" />,
          iconBg: 'bg-emerald-100 text-emerald-700',
          badge: 'bg-emerald-100 text-emerald-700',
          dot: 'bg-emerald-500'
        });
      }
    });
  }

  // Sort chronologically newest first
  return events.sort((a, b) => (b.ts || 0) - (a.ts || 0));
}

// (buildMetrics/orderPhase removed; superseded by ticket-driven modal)
